import { resolveScannedQr } from "@/lib/resolveScannedQr";
import { useEffect, useMemo, useRef, useState } from "react";
import { Html5Qrcode } from "html5-qrcode";
import { format } from "date-fns";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { MapPin, Camera, CameraOff, CheckCircle2, ShieldAlert, Crosshair, CloudOff, RefreshCw, ArrowLeft } from "lucide-react";
import { useOfflineQueue } from "@/hooks/useOfflineQueue";
import { QueuedScanStatus } from "./QueuedScanStatus";
import { getHodAllowedClasses, isHodOnly } from "@/lib/hodClassMapping";

interface Group {
  id: string;
  name: string;
  semester: string;
  class: string;
  site_id: string | null;
  site_name_override: string | null;
  lat_override: number | null;
  lng_override: number | null;
  radius_override_m: number | null;
  approval_status: string;
  scan_start_time: string | null;
  scan_end_time: string | null;
  max_radius_m: number | null;
  field_sites: { name: string; latitude: number; longitude: number; radius_m: number } | null;
}

interface Member {
  student_id: string;
  students: { id: string; first_name: string; last_name: string; student_number: string; barcode_academic: string | null } | null;
}

function haversine(a: { lat: number; lng: number }, b: { lat: number; lng: number }) {
  const R = 6371000;
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const x = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(x), Math.sqrt(1 - x));
}

function siteKey(g: Group): string {
  return g.site_id ?? `override:${g.name}:${g.lat_override}:${g.lng_override}`;
}
function siteLabel(g: Group): string {
  return g.field_sites?.name ?? g.site_name_override ?? "Ad-hoc site";
}

export function StaffRotationScanPanel() {
  const { user, roles } = useAuth();
  const { addToQueue, queue, isOnline, isSyncing } = useOfflineQueue();

  const [todayGroups, setTodayGroups] = useState<Group[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedSiteKey, setSelectedSiteKey] = useState<string | null>(null);
  const [selectedGroup, setSelectedGroup] = useState<Group | null>(null);
  const [members, setMembers] = useState<Member[]>([]);
  const [scannedToday, setScannedToday] = useState<Set<string>>(new Set());
  const [coords, setCoords] = useState<{ lat: number; lng: number } | null>(null);
  const [scanning, setScanning] = useState(false);
  const scannerRef = useRef<Html5Qrcode | null>(null);
  const todayStr = format(new Date(), "yyyy-MM-dd");
  const [scanDate, setScanDate] = useState<string>(todayStr);
  const [grantDates, setGrantDates] = useState<string[]>([]);
  const today = scanDate;

  // Load all approved groups whose placement date is today (department-scoped for HODs)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      // 1. Today's placement rows → group ids
      const { data: dateRows } = await supabase
        .from("field_group_dates")
        .select("group_id")
        .eq("placement_date", today);
      const groupIds = Array.from(new Set(((dateRows ?? []) as any[]).map((r) => r.group_id)));
      if (groupIds.length === 0) {
        if (!cancelled) { setTodayGroups([]); setLoading(false); }
        return;
      }
      // 2. Fetch the groups (RLS handles HOD scoping; for safety we also filter client-side)
      let q = supabase
        .from("field_groups")
        .select("*, field_sites(name, latitude, longitude, radius_m)")
        .in("id", groupIds)
        .eq("approval_status", "approved");
      const { data } = await q;
      let groups = ((data ?? []) as any[]) as Group[];
      // Client-side defence-in-depth for HOD-only users
      if (isHodOnly(roles ?? [])) {
        const allowed = new Set(getHodAllowedClasses(roles ?? []).map((c) => c.toUpperCase()));
        groups = groups.filter((g) => allowed.has((g.class ?? "").toUpperCase()));
      }
      groups.sort((a, b) => siteLabel(a).localeCompare(siteLabel(b)) || a.name.localeCompare(b.name));
      if (!cancelled) { setTodayGroups(groups); setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [today, roles?.join(",")]);

  // Continuous geolocation
  useEffect(() => {
    if (!navigator.geolocation) return;
    const id = navigator.geolocation.watchPosition(
      (pos) => setCoords({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
      () => {},
      { enableHighAccuracy: true, maximumAge: 15000 },
    );
    return () => navigator.geolocation.clearWatch(id);
  }, []);

  // Load members + today's scans for the selected group
  useEffect(() => {
    if (!selectedGroup) { setMembers([]); setScannedToday(new Set()); return; }
    (async () => {
      const [{ data: m }, { data: ar }] = await Promise.all([
        supabase
          .from("field_group_members")
          .select("student_id, students(id, first_name, last_name, student_number, barcode_academic)")
          .eq("group_id", selectedGroup.id),
        supabase
          .from("attendance_records")
          .select("student_id")
          .eq("field_group_id", selectedGroup.id)
          .eq("attendance_date", today)
          .eq("attendance_type", "tuition"),
      ]);
      setMembers((m ?? []) as any);
      setScannedToday(new Set(((ar ?? []) as any[]).map((r) => r.student_id)));
    })();
  }, [selectedGroup?.id, today]);

  // Load active backdate grants for this user on the selected group
  useEffect(() => {
    if (!selectedGroup || !user) { setGrantDates([]); return; }
    (async () => {
      const { data } = await supabase
        .from("rotation_backdate_grants" as any)
        .select("attendance_date, expires_at, consumed_at, revoked_at")
        .eq("field_group_id", selectedGroup.id)
        .eq("granted_to", user.id);
      const now = Date.now();
      const valid = ((data ?? []) as any[])
        .filter((g) => !g.revoked_at && !g.consumed_at && new Date(g.expires_at).getTime() > now)
        .map((g) => g.attendance_date as string)
        .sort();
      setGrantDates(valid);
      // Reset scanDate to today when group changes
      setScanDate(todayStr);
    })();
  }, [selectedGroup?.id, user?.id]);

  // Group sites for the picker
  const sites = useMemo(() => {
    const map = new Map<string, { key: string; label: string; groups: Group[] }>();
    for (const g of todayGroups) {
      const k = siteKey(g);
      if (!map.has(k)) map.set(k, { key: k, label: siteLabel(g), groups: [] });
      map.get(k)!.groups.push(g);
    }
    return Array.from(map.values());
  }, [todayGroups]);

  const groupsAtSite = useMemo(() => {
    if (!selectedSiteKey) return [];
    return sites.find((s) => s.key === selectedSiteKey)?.groups ?? [];
  }, [sites, selectedSiteKey]);

  // Effective site coords for the selected group
  const siteInfo = selectedGroup ? (() => {
    const baseRadius = selectedGroup.radius_override_m ?? selectedGroup.field_sites?.radius_m ?? 150;
    const cap = selectedGroup.max_radius_m;
    return {
      name: siteLabel(selectedGroup),
      lat: selectedGroup.lat_override ?? selectedGroup.field_sites?.latitude ?? null,
      lng: selectedGroup.lng_override ?? selectedGroup.field_sites?.longitude ?? null,
      radius: cap != null && cap < baseRadius ? cap : baseRadius,
    };
  })() : null;

  const distance = siteInfo && coords && siteInfo.lat != null && siteInfo.lng != null
    ? Math.round(haversine(coords, { lat: Number(siteInfo.lat), lng: Number(siteInfo.lng) }))
    : null;
  const insideFence = distance != null && siteInfo != null && distance <= siteInfo.radius;
  const nowHM = format(new Date(), "HH:mm");
  const windowStart = selectedGroup?.scan_start_time?.slice(0, 5) ?? null;
  const windowEnd = selectedGroup?.scan_end_time?.slice(0, 5) ?? null;
  const withinWindow = !windowStart || !windowEnd || (nowHM >= windowStart && nowHM <= windowEnd);
  const canScan = !!selectedGroup && withinWindow && insideFence;

  const queuedForGroup = useMemo(() => {
    if (!selectedGroup) return new Set<string>();
    const ids = new Set<string>();
    for (const item of queue) {
      if (item.type !== "insert") continue;
      const p = item.payload as any;
      if (p?.table === "attendance_records"
          && p?.data?.field_group_id === selectedGroup.id
          && p?.data?.attendance_date === today) {
        ids.add(p.data.student_id);
      }
    }
    return ids;
  }, [queue, selectedGroup, today]);

  async function startScanner() {
    if (!selectedGroup) return;
    setScanning(true);
    try {
      const html = new Html5Qrcode("staff-rotation-reader");
      scannerRef.current = html;
      await html.start(
        { facingMode: "environment" },
        { fps: 10, qrbox: 250 },
        (decoded) => handleScan(decoded),
        () => {},
      );
    } catch (e: any) {
      setScanning(false);
      toast.error("Camera error: " + (e?.message ?? e));
    }
  }
  async function stopScanner() {
    try {
      if (scannerRef.current) { await scannerRef.current.stop(); await scannerRef.current.clear(); }
    } catch {}
    scannerRef.current = null;
    setScanning(false);
  }
  useEffect(() => () => { stopScanner(); }, []);

  const lastScanRef = useRef<{ code: string; ts: number } | null>(null);

  async function handleScan(decoded: string) {
    if (!selectedGroup || !coords || !canScan) return;
    const code = decoded.trim();
    if (!code) return;
    if (lastScanRef.current && lastScanRef.current.code === code && Date.now() - lastScanRef.current.ts < 3000) return;
    lastScanRef.current = { code, ts: Date.now() };

    const resolved = await resolveScannedQr(code, "academic", "rotation_staff_scanner");
    if (!resolved.ok) { toast.error(resolved.message ?? "QR rejected"); return; }
    const lookupCode = resolved.resolvedCode ?? code;

    const member = members.find((m) => m.students?.barcode_academic === lookupCode);
    if (!member?.students) { toast.error("Code not in this group's roster"); return; }
    if (scannedToday.has(member.student_id) || queuedForGroup.has(member.student_id)) {
      toast.info(`${member.students.first_name} already scanned today`);
      return;
    }

    const record = {
      student_id: member.student_id,
      attendance_type: "tuition",
      attendance_date: today,
      field_group_id: selectedGroup.id,
      geo_lat: coords.lat,
      geo_lng: coords.lng,
      status: "present",
      scanned_by: user?.id,
      scanned_at: new Date().toISOString(),
    };

    if (!isOnline) {
      try {
        await addToQueue({
          type: "insert",
          label: `Rotation scan · ${member.students.first_name} ${member.students.last_name}`,
          payload: { table: "attendance_records", data: record },
        });
        toast.success(`📥 Queued ${member.students.first_name} (offline)`);
      } catch (e: any) { toast.error("Could not queue scan: " + (e?.message ?? e)); }
      return;
    }

    const { error } = await supabase.from("attendance_records").insert(record);
    if (error) {
      try {
        await addToQueue({
          type: "insert",
          label: `Rotation scan · ${member.students.first_name} ${member.students.last_name}`,
          payload: { table: "attendance_records", data: record },
        });
        toast.warning(`Saved offline — will retry: ${error.message}`);
      } catch { toast.error(error.message); }
      return;
    }
    setScannedToday((prev) => new Set(prev).add(member.student_id));
    if (scanDate !== todayStr) {
      // Backdate grant is single-use — drop this date from the picker
      setGrantDates((prev) => prev.filter((d) => d !== scanDate));
      setScanDate(todayStr);
      toast.info("Backdated entry recorded — switched back to today");
    }
    toast.success(`✓ ${member.students.first_name} ${member.students.last_name}`);
  }

  if (loading) {
    return <Card><CardContent className="pt-6 text-sm text-muted-foreground">Loading today's placements…</CardContent></Card>;
  }
  if (sites.length === 0) {
    return (
      <Alert>
        <ShieldAlert className="h-4 w-4" />
        <AlertTitle>No placements scheduled today</AlertTitle>
        <AlertDescription>
          There are no approved field groups with a placement date of {format(new Date(), "PP")}{isHodOnly(roles ?? []) ? " in your department" : ""}.
        </AlertDescription>
      </Alert>
    );
  }

  // Step 1: pick site
  if (!selectedSiteKey) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2"><MapPin className="w-4 h-4" /> Step 1 — Select field site</CardTitle>
          <CardDescription>Pick the site you are at. Only sites with a placement scheduled for today are shown.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-2">
          {sites.map((s) => (
            <button
              key={s.key}
              onClick={() => setSelectedSiteKey(s.key)}
              className="w-full text-left border rounded-md px-3 py-2 hover:bg-muted/50 transition flex items-center justify-between"
            >
              <div>
                <p className="font-medium">{s.label}</p>
                <p className="text-xs text-muted-foreground">{s.groups.length} group{s.groups.length === 1 ? "" : "s"} today</p>
              </div>
              <Badge variant="secondary">{s.groups.map((g) => g.class).filter((v, i, a) => a.indexOf(v) === i).join(", ")}</Badge>
            </button>
          ))}
        </CardContent>
      </Card>
    );
  }

  // Step 2: pick group at site
  if (!selectedGroup) {
    return (
      <div className="space-y-3">
        <Button variant="ghost" size="sm" onClick={() => setSelectedSiteKey(null)}>
          <ArrowLeft className="w-4 h-4 mr-1" /> Change site
        </Button>
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2"><MapPin className="w-4 h-4" /> Step 2 — Select group</CardTitle>
            <CardDescription>{sites.find((s) => s.key === selectedSiteKey)?.label}</CardDescription>
          </CardHeader>
          <CardContent>
            <Select onValueChange={(v) => {
              const g = groupsAtSite.find((x) => x.id === v);
              if (g) setSelectedGroup(g);
            }}>
              <SelectTrigger><SelectValue placeholder="Pick a group…" /></SelectTrigger>
              <SelectContent>
                {groupsAtSite.map((g) => (
                  <SelectItem key={g.id} value={g.id}>
                    {g.name} — {g.class} (Sem {g.semester})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Step 3: scan
  const presentCount = scannedToday.size;
  const queuedCount = queuedForGroup.size;
  const total = members.length;
  const accountedFor = presentCount + queuedCount;

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <Button variant="ghost" size="sm" onClick={() => { stopScanner(); setSelectedGroup(null); }}>
          <ArrowLeft className="w-4 h-4 mr-1" /> Change group
        </Button>
        <Button variant="ghost" size="sm" onClick={() => { stopScanner(); setSelectedGroup(null); setSelectedSiteKey(null); }}>
          Change site
        </Button>
      </div>

      <QueuedScanStatus fieldGroupId={selectedGroup.id} />
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <MapPin className="w-4 h-4" /> {siteInfo?.name}
          </CardTitle>
          <CardDescription>
            {selectedGroup.name} • {selectedGroup.class} • Semester {selectedGroup.semester}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {grantDates.length > 0 && (
            <div className="border rounded-md p-2 flex flex-wrap items-center gap-2 bg-amber-50 dark:bg-amber-950/30">
              <span className="text-xs font-medium">Scan date</span>
              <Select value={scanDate} onValueChange={setScanDate}>
                <SelectTrigger className="h-8 w-auto min-w-[180px]"><SelectValue /></SelectTrigger>
                <SelectContent position="popper">
                  <SelectItem value={todayStr}>Today — {format(new Date(), "PP")}</SelectItem>
                  {grantDates.map((d) => (
                    <SelectItem key={d} value={d}>Backdated — {format(new Date(d), "PP")}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {scanDate !== todayStr && (
                <Badge variant="outline" className="text-[10px]">Superadmin-granted backdate · single use</Badge>
              )}
            </div>
          )}
          {!withinWindow ? (
            <Alert variant="destructive">
              <ShieldAlert className="h-4 w-4" />
              <AlertTitle>Outside scan window</AlertTitle>
              <AlertDescription>
                Scanning is only allowed between <strong>{windowStart}</strong> and <strong>{windowEnd}</strong>. It is currently {nowHM}.
              </AlertDescription>
            </Alert>
          ) : !coords ? (
            <Alert>
              <Crosshair className="h-4 w-4" />
              <AlertTitle>Waiting for location…</AlertTitle>
              <AlertDescription>Please allow location access in your browser.</AlertDescription>
            </Alert>
          ) : !insideFence ? (
            <Alert variant="destructive">
              <ShieldAlert className="h-4 w-4" />
              <AlertTitle>You're outside the geofence</AlertTitle>
              <AlertDescription>
                You must be at <strong>{siteInfo?.name}</strong> (within {siteInfo?.radius}m) to scan.
                {distance != null && ` You are currently ${distance}m away.`}
              </AlertDescription>
            </Alert>
          ) : (
            <Alert>
              <CheckCircle2 className="h-4 w-4" />
              <AlertTitle>Ready to scan</AlertTitle>
              <AlertDescription>
                {distance}m from site centre.
                {windowStart && windowEnd && ` Window ${windowStart}–${windowEnd}.`}
              </AlertDescription>
            </Alert>
          )}

          {(!isOnline || queuedCount > 0) && (
            <Alert>
              {isOnline
                ? <RefreshCw className={`h-4 w-4 ${isSyncing ? "animate-spin" : ""}`} />
                : <CloudOff className="h-4 w-4" />}
              <AlertTitle>
                {isOnline
                  ? (isSyncing ? "Syncing queued scans…" : `${queuedCount} scan${queuedCount === 1 ? "" : "s"} pending sync`)
                  : "You are offline — scans will be queued"}
              </AlertTitle>
              <AlertDescription>
                Queued scans keep their original timestamp and are sent automatically once you reconnect.
              </AlertDescription>
            </Alert>
          )}

          <div className="grid grid-cols-3 gap-2 text-center">
            <div className="border rounded p-2"><p className="text-2xl font-bold">{total}</p><p className="text-[11px] text-muted-foreground">Roster</p></div>
            <div className="border rounded p-2">
              <p className="text-2xl font-bold text-primary">{presentCount}</p>
              <p className="text-[11px] text-muted-foreground">
                Scanned{queuedCount > 0 ? <> · <span className="font-medium">+{queuedCount} queued</span></> : null}
              </p>
            </div>
            <div className="border rounded p-2"><p className="text-2xl font-bold">{Math.max(total - accountedFor, 0)}</p><p className="text-[11px] text-muted-foreground">Pending</p></div>
          </div>

          <div className="flex justify-center">
            {!scanning ? (
              <Button onClick={startScanner} disabled={!canScan}>
                <Camera className="w-4 h-4 mr-1" /> Start scanner
              </Button>
            ) : (
              <Button variant="outline" onClick={stopScanner}>
                <CameraOff className="w-4 h-4 mr-1" /> Stop scanner
              </Button>
            )}
          </div>
          <div id="staff-rotation-reader" className="w-full max-w-sm mx-auto" />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Roster</CardTitle>
          <CardDescription>Scan each student's Academic QR code.</CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-12">SN</TableHead>
                <TableHead>Student</TableHead>
                <TableHead>Reg #</TableHead>
                <TableHead className="text-right">Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {members.map((m, i) => (
                <TableRow key={m.student_id}>
                  <TableCell>{i + 1}</TableCell>
                  <TableCell>{m.students ? `${m.students.first_name} ${m.students.last_name}` : "—"}</TableCell>
                  <TableCell className="font-mono text-xs">{m.students?.student_number ?? "—"}</TableCell>
                  <TableCell className="text-right">
                    {scannedToday.has(m.student_id) ? (
                      <Badge><CheckCircle2 className="h-3 w-3 mr-1" /> Present</Badge>
                    ) : queuedForGroup.has(m.student_id) ? (
                      <Badge variant="outline"><CloudOff className="h-3 w-3 mr-1" /> Queued</Badge>
                    ) : (
                      <Badge variant="secondary">Pending</Badge>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
